import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, X, ChevronLeft, ChevronRight, BadgeCheck, ArrowRight } from 'lucide-react';
import { fetchClientReviews } from '../api';
import { opt } from '../lib/media';

const src = opt;

export function StarRow({ value, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= value ? 'fill-gold text-gold' : 'fill-transparent text-burgundy-900/25'} />
      ))}
    </div>
  );
}

function Lightbox({ images, index, onClose, setIndex }) {
  const go = (d) => setIndex((index + d + images.length) % images.length);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  return (
    <div data-testid="review-lightbox" className="fixed inset-0 z-[90] bg-burgundy-950/95 flex items-center justify-center p-4" onClick={onClose}>
      <button data-testid="review-lightbox-close" onClick={onClose} className="absolute top-5 right-5 text-cream/80 hover:text-gold"><X size={28} /></button>
      {images.length > 1 && <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 md:left-8 text-cream/80 hover:text-gold"><ChevronLeft size={36} /></button>}
      <img src={src(images[index], 1600)} alt="Client look" className="max-h-[88vh] max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
      {images.length > 1 && <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 md:right-8 text-cream/80 hover:text-gold"><ChevronRight size={36} /></button>}
    </div>
  );
}

function ReviewCard({ r, onOpen }) {
  const imgs = r.images || [];
  return (
    <article data-testid={`client-review-${r.id}`} className="break-inside-avoid mb-6 bg-white border border-gold/15 overflow-hidden fade-up">
      {imgs.length > 0 && (
        <div className={`grid gap-0.5 ${imgs.length > 1 ? 'grid-cols-2' : ''}`}>
          {imgs.slice(0, 4).map((u, i) => (
            <button key={u} onClick={() => onOpen(imgs, i)} className={`relative overflow-hidden img-zoom ${imgs.length === 1 ? 'aspect-[4/5]' : 'aspect-square'}`}>
              <img src={src(u, 600)} alt={`${r.name} wearing HITÉRA`} loading="lazy" decoding="async" width="480" height="600" className="w-full h-full object-cover" />
              {i === 3 && imgs.length > 4 && <span className="absolute inset-0 bg-burgundy-950/60 text-cream font-display text-2xl flex items-center justify-center">+{imgs.length - 4}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <StarRow value={r.rating} size={15} />
          {r.featured && <span className="text-[10px] tracking-wideluxe uppercase text-gold-dark">Featured</span>}
        </div>
        <p className="font-display text-xl text-burgundy-900 leading-snug italic">“{r.experience}”</p>
        {r.productName && <p className="text-[11px] tracking-wideluxe uppercase text-gold-dark mt-4">Wore · {r.productName}</p>}
        {r.loved && <p className="text-sm text-burgundy-900/65 mt-3"><span className="text-gold-dark tracking-wide uppercase text-[10px] mr-1.5">Loved most</span>{r.loved}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-[11px] text-burgundy-900/55 uppercase tracking-wide">
          {r.fitFeel && <span>Fit · {r.fitFeel}</span>}
          <span>Fit {r.fit}/5</span><span>Quality {r.quality}/5</span>
        </div>
        <div className="mt-5 pt-4 border-t border-gold/15 flex items-end justify-between gap-3">
          <div>
            <p className="font-display text-lg text-burgundy-900 flex items-center gap-1.5">{r.name} <BadgeCheck size={15} className="text-gold-dark" /></p>
            <p className="text-[11px] text-burgundy-900/50">{[r.city, r.occasion].filter(Boolean).join(' · ')}</p>
          </div>
          {r.productId && <Link to={`/product/${r.productId}`} className="text-[10px] tracking-wideluxe uppercase border-b border-gold text-burgundy-900 pb-0.5 shrink-0">Shop Look</Link>}
        </div>
      </div>
    </article>
  );
}

export default function ClientReviewsWall({ productId, limit = 60, title = 'Client Diaries', eyebrow = 'Reviews · Real Clients', showCta = true }) {
  const [reviews, setReviews] = useState([]);
  const [box, setBox] = useState(null);

  useEffect(() => {
    fetchClientReviews({ ...(productId ? { productId } : {}), limit: 200 }).then((d) => setReviews(Array.isArray(d) ? d : [])).catch(() => {});
  }, [productId]);

  if (!reviews.length) return null;
  const avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;

  return (
    <section data-testid="client-reviews-wall" className="py-20 md:py-24 bg-cream/40">
      <div className="max-w-[1300px] mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-gold-dark tracking-luxe text-xs uppercase mb-3">{eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl text-burgundy-900">{title}</h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <StarRow value={Math.round(avg)} size={18} />
            <span data-testid="client-reviews-average" className="text-sm text-burgundy-900/70">{avg.toFixed(1)} · {reviews.length} review{reviews.length === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
          {reviews.slice(0, limit).map((r) => <ReviewCard key={r.id} r={r} onOpen={(images, index) => setBox({ images, index })} />)}
        </div>
        {showCta && (
          <div className="text-center mt-10">
            <Link data-testid="write-review-link" to="/review" className="inline-flex items-center gap-3 border-b border-gold text-burgundy-900 pb-1 text-xs tracking-wideluxe uppercase hover:text-gold-dark transition-colors">Share Your HITÉRA Story <ArrowRight size={15} /></Link>
          </div>
        )}
      </div>
      {box && <Lightbox images={box.images} index={box.index} setIndex={(i) => setBox((b) => ({ ...b, index: i }))} onClose={() => setBox(null)} />}
    </section>
  );
}
